/**
 * Perf HUD - in-app performance instrumentation for native devices.
 *
 * Captures:
 *   - Route transitions (URL → URL with elapsed ms)
 *   - React Query timings (queryKey → fetch ms, cache hit/miss)
 *   - Long tasks (>50ms blocking work)
 *
 * Enable/disable:
 *   - URL `?perf=1` enables and persists; `?perf=0` disables and clears.
 *   - 5-finger tap anywhere toggles at runtime (native-friendly).
 *   - In the overlay, the × button disables it.
 *   - Programmatic: `window.__elefaPerf.disable()`.
 *
 * Default: OFF. Zero overhead when disabled (no listeners attached).
 */

const STORAGE_KEY = "elefa_perf_hud";
const MAX_ENTRIES = 80;

export type PerfEntry = {
  id: number;
  ts: number;
  kind: "route" | "query" | "longtask" | "mark";
  label: string;
  ms?: number;
  detail?: string;
};

type Listener = (entries: PerfEntry[]) => void;

let entries: PerfEntry[] = [];
let listeners = new Set<Listener>();
let nextId = 1;

export function isPerfEnabled(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export function enablePerf() {
  try {
    localStorage.setItem(STORAGE_KEY, "1");
  } catch {
    /* ignore */
  }
}

export function disablePerf() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
  entries = [];
  notify();
}

export function clearPerf() {
  entries = [];
  notify();
}

export function recordPerf(entry: Omit<PerfEntry, "id" | "ts">) {
  if (!isPerfEnabled()) return;
  entries.push({ ...entry, id: nextId++, ts: Date.now() });
  if (entries.length > MAX_ENTRIES) entries = entries.slice(-MAX_ENTRIES);
  notify();
}

export function getPerfEntries(): PerfEntry[] {
  return entries;
}

export function subscribePerf(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function notify() {
  for (const fn of listeners) fn(entries);
}

/**
 * One-shot bootstrap. Handles ?perf=… URL flag and exposes a global
 * window.__elefaPerf so QA can toggle from Safari console too.
 */
export function bootstrapPerf() {
  if (typeof window === "undefined") return;
  try {
    const url = new URL(window.location.href);
    const flag = url.searchParams.get("perf");
    if (flag === "1") enablePerf();
    if (flag === "0") disablePerf();
  } catch {
    /* ignore */
  }
  (window as unknown as { __elefaPerf?: unknown }).__elefaPerf = {
    enable: enablePerf,
    disable: disablePerf,
    clear: clearPerf,
    entries: getPerfEntries,
  };
}
